import React, { useEffect, useState } from "react";
import { Trophy, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { REACTIONS } from "@/components/reactions";

/** Compact trophy case panel — displays counts of each reaction kind the
 *  given user has earned across all their comments. */
export default function TrophyCase({ userId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    if (!userId) return;
    setLoading(true);
    api.get(`/u/${userId}/trophies`)
      .then(({ data }) => { if (alive) setData(data); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [userId]);

  return (
    <section
      data-testid="trophy-case"
      className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded-2xl p-4"
    >
      <div className="flex items-center gap-2 mb-3">
        <Trophy className="w-4 h-4 text-[var(--tt-yellow)]" />
        <h3 className="font-display font-bold text-sm tracking-tight">Trophy Case</h3>
        {data?.total > 0 && (
          <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-[var(--tt-yellow)] font-mono-tt">
            {data.total} earned
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center justify-center gap-2 py-4 text-xs text-[var(--tt-text-muted)]">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading...
        </div>
      ) : !data || data.total === 0 ? (
        <div className="text-xs text-[var(--tt-text-muted)] text-center py-4">
          No trophies yet — earn them by posting comments others react to.
        </div>
      ) : (
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          {REACTIONS.map((r) => {
            const n = data.counts?.[r.kind] || 0;
            return (
              <div
                key={r.kind}
                data-testid={`trophy-${r.kind}`}
                title={`${r.label} · ${n}`}
                className={`flex flex-col items-center gap-1 px-2 py-2 rounded-xl border
                  ${n > 0
                    ? "bg-[var(--tt-yellow)]/[0.06] border-[var(--tt-yellow)]/30"
                    : "bg-white/[0.02] border-[var(--tt-border)] opacity-50"}`}
              >
                <img src={r.url} alt={r.label} className="w-6 h-6" />
                <div className={`text-xs font-mono-tt font-bold ${n > 0 ? "text-white" : "text-[var(--tt-text-faint)]"}`}>
                  {n}
                </div>
                <div className="text-[9px] uppercase tracking-wider text-[var(--tt-text-faint)] text-center leading-tight">
                  {r.label}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
